import { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "./ui/dialog";
import { ShareNetwork, Copy, EnvelopeSimple, ChatDots, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

export default function ShareWithBookkeeper({ variant = "default" }) {
  const { activeCompany, activeCompanyId } = useCompany();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [bookkeepers, setBookkeepers] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [openQuestions, setOpenQuestions] = useState([]);
  const [copied, setCopied] = useState(false);
  const [month] = useState(currentMonth());

  useEffect(() => {
    if (!open || !activeCompanyId) return;
    (async () => {
      try {
        const [itemsRes, qRes] = await Promise.all([
          api.get(`/monthly-items?company_id=${activeCompanyId}&month=${month}`),
          api.get(`/questions?company_id=${activeCompanyId}&status=unresolved`),
        ]);
        setPendingItems(itemsRes.data.filter((i) => i.status === "pending"));
        setOpenQuestions(qRes.data);
      } catch (e) {
        console.warn("Failed to load month status:", e);
      }
      if (user?.role === "owner") {
        try {
          const { data } = await api.get("/users");
          setBookkeepers(data.filter((u) => u.role === "bookkeeper"));
        } catch (e) {
          console.warn("Failed to load bookkeepers:", e);
        }
      }
    })();
  }, [open, activeCompanyId, user, month]);

  const shareUrl = useMemo(() => {
    const base = window.location.origin;
    return `${base}/recurring?company=${activeCompanyId}`;
  }, [activeCompanyId]);

  const monthLabel = new Date(month + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const summaryText = useMemo(() => {
    const lines = [];
    lines.push(`${activeCompany?.name || "Entity"} — ${monthLabel} status`);
    lines.push("");
    if (pendingItems.length === 0) {
      lines.push("All recurring items have been submitted.");
    } else {
      lines.push(`Outstanding items (${pendingItems.length}):`);
      pendingItems.slice(0, 12).forEach((i) => lines.push(`  • ${i.name}${i.vendor ? ` (${i.vendor})` : ""}`));
      if (pendingItems.length > 12) lines.push(`  • +${pendingItems.length - 12} additional`);
    }
    if (openQuestions.length) {
      lines.push("");
      lines.push(`Open inquiries awaiting response: ${openQuestions.length}`);
    }
    lines.push("");
    lines.push(`View in Bookkeeping Portal: ${shareUrl}`);
    return lines.join("\n");
  }, [activeCompany, monthLabel, pendingItems, openQuestions, shareUrl]);

  const subject = `${activeCompany?.name || "Entity"} — ${monthLabel} status`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: subject, text: summaryText, url: shareUrl });
      } catch (err) {
        // User cancelled the native share sheet — Web Share API throws AbortError.
        // This is expected and not an error worth surfacing.
        if (err?.name !== "AbortError") {
          console.warn("Native share failed:", err);
        }
      }
    } else {
      handleCopy();
    }
  };

  const handleEmail = (toEmail) => {
    const mailto = `mailto:${toEmail || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(summaryText)}`;
    window.location.href = mailto;
  };

  const handleSms = () => {
    const sms = `sms:?&body=${encodeURIComponent(summaryText)}`;
    window.location.href = sms;
  };

  const triggerClasses =
    variant === "icon"
      ? "p-2 hover:bg-[#F5F5F5] transition-all border border-[#E5E5E5]"
      : variant === "compact"
      ? "px-3 py-2 border border-[#E5E5E5] hover:bg-white hover:border-[#0A0A0A] transition-all text-sm flex items-center gap-2"
      : "px-4 py-2.5 border border-[#E5E5E5] hover:bg-white hover:border-[#0A0A0A] transition-all font-heading font-medium flex items-center gap-2";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className={triggerClasses} data-testid="share-with-bookkeeper-button">
          <ShareNetwork size={variant === "icon" ? 18 : 16} weight="duotone" />
          {variant !== "icon" && <span>Share with bookkeeper</span>}
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShareNetwork size={20} weight="duotone" className="text-[#002FA7]" />
            Share status with bookkeeper
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <div className="label-tag mb-2">Message preview</div>
            <pre className="text-xs bg-[#F5F5F5] border border-[#E5E5E5] p-3 whitespace-pre-wrap font-mono max-h-60 overflow-y-auto" data-testid="share-preview">
{summaryText}
            </pre>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleNativeShare}
              className="btn-ikb w-full py-3 flex items-center justify-center gap-2"
              data-testid="share-native-button"
            >
              <ShareNetwork size={18} weight="bold" />
              {typeof navigator !== "undefined" && navigator.share ? "Share…" : "Copy message"}
            </button>

            {bookkeepers.length > 0 && (
              <div className="grid grid-cols-1 gap-2">
                {bookkeepers.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => handleEmail(b.email)}
                    className="px-3 py-2.5 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all flex items-center justify-between text-sm"
                    data-testid={`share-email-${b.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <EnvelopeSimple size={16} weight="duotone" />
                      <span className="truncate">Email {b.name}</span>
                    </span>
                    <span className="text-xs text-[#525252] truncate ml-2">{b.email}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleSms}
                className="px-3 py-2.5 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all flex items-center justify-center gap-2 text-sm"
                data-testid="share-sms-button"
              >
                <ChatDots size={16} weight="duotone" /> Text message
              </button>
              <button
                onClick={handleCopy}
                className="px-3 py-2.5 border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all flex items-center justify-center gap-2 text-sm"
                data-testid="share-copy-button"
              >
                {copied ? <CheckCircle size={16} weight="fill" className="text-[#10B981]" /> : <Copy size={16} weight="duotone" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="pt-2 border-t border-[#E5E5E5]">
              <div className="label-tag mb-1">Direct link</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 px-2 py-1.5 border border-[#E5E5E5] text-xs font-mono bg-[#F5F5F5]"
                  onFocus={(e) => e.target.select()}
                  data-testid="share-link-input"
                />
                <button
                  onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }}
                  className="px-3 py-1.5 border border-[#E5E5E5] hover:bg-[#F5F5F5] text-xs"
                  data-testid="share-link-copy"
                >
                  <Copy size={14} weight="duotone" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 border border-[#E5E5E5] hover:bg-[#F5F5F5]"
          >Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
